import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferSource } from '@cds';
import { FavouriteType, FavouritesService, FavouritesViewModel, isFixtureFavourite } from '@frontend/sports/favourites/core/feature';
import { GridEvent } from '@frontend/sports/grid/core/feature/model';
import { GridActions, IGridRootState, IGridState, gridStateSelector } from '@frontend/sports/grid/core/feature/store';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { Store } from '@ngrx/store';
import { difference, flattenDeep } from 'lodash-es';
import { filter, map, pairwise, withLatestFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class GridFavouriteService {
    private favouriteService = inject(FavouritesService);
    private store = inject<Store<IGridRootState>>(Store);
    private user = inject(UserService);

    constructor() {
        const productUtils = inject(SportsProductUtils);

        this.favouriteService.change
            .pipe(
                productUtils.filterSportsEmitLast(),
                map((change) => change.favourites.filter(isFixtureFavourite)),
                pairwise(),
                map(([previousChange, currentChange]) => [
                    ...difference(previousChange, currentChange), // added stuff
                    ...difference(currentChange, previousChange), // removed stuff
                ]),
                filter((change) => change.length > 0),
                withLatestFrom(this.store.select(gridStateSelector)),
                takeUntilDestroyed(),
            )
            .subscribe(([change, state]) => {
                this.updateFavourited(change, state);
            });
    }

    isFavourited(event: GridEvent): boolean | undefined {
        if (!this.user.isAuthenticated) {
            return;
        }

        return !!this.favouriteService.get(event.id, event.sport.id, this.getType(event));
    }

    private updateFavourited(diff: FavouritesViewModel[], state: IGridState): void {
        if (diff.length === 0) {
            return;
        }

        for (const grid of Object.values(state)) {
            const events = flattenDeep<GridEvent>(grid.groups.map((group) => group.events));
            const changed = events.filter((event) => diff.some((c) => c.itemId === event.id));

            for (const change of changed) {
                change.favourited = this.isFavourited(change);
                this.store.dispatch(
                    GridActions.updateEvents({
                        payload: {
                            id: grid.id,
                            event: change,
                        },
                    }),
                );
            }
        }
    }

    private getType(event: GridEvent): FavouriteType {
        if (!event.offerSource || event.offerSource === OfferSource.V1) {
            return FavouriteType.Fixture;
        }

        return FavouriteType.FixtureV2;
    }
}
